import { isDefined } from "@clipboard-health/util-ts";
import { type RushFeeConfig } from "@src/appV2/Auth/types";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { differenceInMinutes } from "date-fns";

/**
 * Returns true when `shiftStart` falls inside the workplace's rush-fee window — i.e. the
 * shift is in the future but starts within `rushFeeConfig.period` hours, AND the workplace
 * has a non-zero `differential` configured.
 *
 * The wrapper and both inner fields can each be null/undefined per the canonical Zod
 * contract; the function returns false for any of those nullish cases. Used by both the
 * single-shift posting flow (`useRushFeeCheck`) and the bulk-posting flow
 * (`useBulkPostingDraft`) so both surfaces agree on what counts as a rush-fee shift.
 */
export function isWithinRushFeeWindow(
  rushFeeConfig: RushFeeConfig | null | undefined,
  shiftStart: Date
): boolean {
  const differential = rushFeeConfig?.differential;
  const period = rushFeeConfig?.period;
  if (!isDefined(differential) || !isDefined(period) || differential <= 0 || period <= 0) {
    return false;
  }

  const hoursUntilShift = differenceInMinutes(shiftStart, getServerNow()) / 60;
  return hoursUntilShift > 0 && hoursUntilShift <= period;
}

export function formatRushFeeAmount(differential: number): string {
  return `$${differential.toFixed(2)}/hour`;
}

export function hasActiveLegacyRushFee(rushFeeConfig: RushFeeConfig | null | undefined): boolean {
  const differential = rushFeeConfig?.differential;
  const period = rushFeeConfig?.period;
  return isDefined(differential) && isDefined(period) && differential > 0 && period > 0;
}
